import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { SpinnerComponent, fade, fadeUp, hshrink, vshrink } from 'shared';

import { SocketService } from '../../../services';
import { AppStateService } from '../../../services/state';

interface Notification {
    title: Observable<string>;
    subtitle?: Observable<string>;
    showReconnectButton: boolean;
    icon: string;
}

const RECONNECT_ATTEMPTS = 2;

@Component({
    selector: 'app-socket-overlay',
    templateUrl: './socket-overlay.component.html',
    styleUrls: ['./socket-overlay.component.scss'],
    animations: [fadeUp('notification'), vshrink(), hshrink(), fade()],
    imports: [SpinnerComponent, AsyncPipe, TranslatePipe],
})
export class SocketOverlayComponent {
    private readonly socketService = inject(SocketService);
    private readonly translate = inject(TranslateService);

    protected notification$: Observable<Notification>;

    public constructor() {
        const appState = inject(AppStateService);

        this.notification$ = combineLatest([
            appState.socketStatus.distinctStream,
            appState.loggedInWithTenant.distinctStream,
        ]).pipe(
            debounceTime(0),
            map(([socketStatus, loggedInWithTenant]) => {
                switch (socketStatus.status) {
                    case 'CONNECTING':
                        return {
                            title: this.translate.stream('comp.socket-overlay.CONNECTING.title'),
                            showReconnectButton: false,
                            icon: 'spinner',
                        };
                    case 'RECONNECTING':
                        if (socketStatus.attempt >= RECONNECT_ATTEMPTS) {
                            return {
                                title: this.translate.stream('comp.socket-overlay.RECONNECTING.title'),
                                subtitle: this.translate.stream('comp.socket-overlay.RECONNECTING.subtitle'),
                                showReconnectButton: false,
                                icon: 'spinner',
                            };
                        }
                        return null;
                    case 'DROPPED':
                        return {
                            title: this.translate.stream('comp.socket-overlay.DROPPED.title'),
                            subtitle: this.translate.stream('comp.socket-overlay.DROPPED.subtitle'),
                            showReconnectButton: true,
                            icon: 'wifi_off',
                        };
                    case 'DISCONNECTED':
                        if (loggedInWithTenant) {
                            return {
                                title: this.translate.stream('comp.socket-overlay.DISCONNECTED.title'),
                                subtitle: this.translate.stream('comp.socket-overlay.DISCONNECTED.subtitle'),
                                showReconnectButton: true,
                                icon: 'wifi_off',
                            };
                        }
                        return null;
                    case 'CONNECTED':
                    default:
                        return null;
                }
            })
        );
    }

    protected onReconnectPressed(): void {
        this.socketService.connect();
    }
}
